import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Exporting from "highcharts/modules/exporting";
import OfflineExporting from "highcharts/modules/offline-exporting";
import React, { FC, useMemo, useRef } from "react";

Exporting(Highcharts);
OfflineExporting(Highcharts);

interface DataSeries {
  assetId: string;
  name: string;
  data: (number | null)[];
  lightIntensity?: (number | null)[];
}

interface StepChartProps {
  data: DataSeries[];
  tooltipOutside?: boolean;
  yAxisTitleText?: string;
  fileName?: string;
  chartName?: string;
  xAxisTitle?: string;
}

// 15-minute intervals (96)
const MINUTES_15_INTERVALS = Array.from({ length: 96 }, (_, i) => {
  const hour = Math.floor(i / 4);
  const minute = (i % 4) * 15;
  return `${hour}:${minute.toString().padStart(2, "0")}`;
});

function generateColors(n: number) {
  const colors: string[] = [];
  for (let i = 0; i < n; i++) {
    const hue = Math.round((360 / Math.max(1, n)) * i) % 360;
    colors.push(`hsl(${hue} 70% 55%)`);
  }
  return colors;
}

const formatTooltip: Highcharts.TooltipFormatterCallbackFunction = function () {
  const header = `<span style="display: block;">${this.x}</span>`;

  const points = this.points || [];

  const body = points
    .map((point) => {
      let status = "No Data";
      if (point.y === 1) status = "On";
      else if (point.y === 0) status = "Off";

      const lightIntensity = (point.point.options as any).lightIntensity;

      return `
          <span style="color:${point.color}">●</span>
         ${point.series.name} <strong>: ${status}</strong>
         ${lightIntensity !== null && lightIntensity !== undefined
          ? `<br/><span style="display: block;margin-left: 10px;">Light Intensity: <strong>${lightIntensity}%</strong></span>`
          : ""
        }
        `;
    })
    .join("");

  return header + body;
};

const StepLineChartComponent: FC<StepChartProps> = ({
  data,
  tooltipOutside = true,
  yAxisTitleText = "Light Status (0=Off, 1=On)",
  fileName = "Light Status Chart",
  chartName = "Light Operation Status Analytics",
  xAxisTitle = "Time (15 min interval)",
}) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  const builtSeries = useMemo(() => {
    const colors = generateColors(data?.length || 0);
    return (data || []).map((s, idx) => ({
      type: "line" as const,
      step: "left" as const,
      showInLegend: true,
      name: s.name,
      color: colors[idx],
      connectNulls: true,
      marker: { enabled: true, radius: 3 },
      data: (s.data || [])?.map((y, i) => ({
        y,
        lightIntensity: s?.lightIntensity?.[i] ?? null,
      })),
    }));
  }, [data]);

  const chartOptions: Highcharts.Options = {
    chart: {
      style: {
        fontFamily: '"Cabin", Helvetica, Arial, sans-serif',
      },
      zooming: {
        type: "x",
      },
      animation: {
        duration: 1200,
      },
    },
    lang: { noData: "No data available" },
    title: { text: "" },
    credits: { enabled: false },
    xAxis: {
      categories: MINUTES_15_INTERVALS,
      title: { text: xAxisTitle },
      crosshair: true,
    },
    yAxis: {
      min: 0,
      max: 1,
      tickInterval: 1,
      title: { text: yAxisTitleText },
      labels: { format: "{value}" },
    },
    tooltip: {
      // Keep modal tooltips inside the modal's stacking context.
      outside: tooltipOutside,
      shared: true,
      useHTML: true,
      formatter: formatTooltip,
    },
    legend: { enabled: true, x: 40, y: 0 },
    exporting: {
      enabled: true,
      fallbackToExportServer: false,
      filename: fileName,
      sourceWidth: 1200,
      sourceHeight: 500,
      scale: 1,
      chartOptions: chartName
        ? {
          chart: {
            height: "80%",
            width: "100%",
            spacingTop: 30,
            marginTop: 100,
          },
          title: {
            text: chartName,
            align: "center",
            style: { fontSize: "24px", fontWeight: "bold" },
          },
        }
        : { chart: { height: "80%", width: "100%" } },
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            "printChart",
            "separator",
            "downloadPNG",
            "downloadJPEG",
            "downloadPDF",
            "downloadSVG",
          ],
        },
      },
    },
    plotOptions: {
      line: {
        lineWidth: 2,
        step: "left",
        marker: {
          enabled: true,
          radius: 3,
        },
      },
    },
    series: builtSeries as Highcharts.SeriesOptionsType[],
  };

  return (
    <div>
      {builtSeries?.length ? (
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          ref={chartComponentRef}
        />
      ) : (
        <div
          style={{
            height: "300px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#D9D9D9",
            fontSize: "22px",
            fontWeight: "600",
            color: "#6d6d6d",
          }}
        >
          No Data Available
        </div>
      )}
    </div>
  );
};

export default React.memo(StepLineChartComponent);
